import { ChecklistModuleDefinition } from '../../types/due-diligence';

export const floodClimateModule: ChecklistModuleDefinition = {
  code: 'flood_climate',
  name: 'Flood & Climate',
  description: 'Flood zones, fire risk, weather concerns',
  categories: [
    {
      code: 'flood_zones',
      name: 'Flood Zones',
      items: [
        {
          code: 'fema_flood_zone',
          title: 'FEMA flood zone determination',
          description: 'Check FEMA flood maps',
          isGroupTask: false,
          dataFields: [
            { key: 'flood_zone', label: 'Flood Zone', type: 'select', options: [
              { value: 'x', label: 'Zone X (Minimal Risk)' },
              { value: 'x_shaded', label: 'Zone X Shaded (Moderate Risk)' },
              { value: 'a', label: 'Zone A (High Risk)' },
              { value: 'ae', label: 'Zone AE (High Risk w/ BFE)' },
              { value: 'v', label: 'Zone V (Coastal High Risk)' },
              { value: 've', label: 'Zone VE (Coastal w/ BFE)' },
            ]},
            { key: 'percent_in_flood_zone', label: '% in Flood Zone', type: 'number', unit: 'percent' },
            { key: 'base_flood_elevation', label: 'Base Flood Elevation', type: 'number', unit: 'feet' },
            { key: 'map_date', label: 'Map Date', type: 'date' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Building site in high-risk flood zone' },
            { flag: 'red', condition: 'Significant acreage in flood zone' },
            { flag: 'yellow', condition: 'Some flood zone exposure' },
            { flag: 'green', condition: 'Zone X - minimal flood risk' },
          ],
        },
        {
          code: 'flood_history',
          title: 'Flood history',
          description: 'Historical flooding on property',
          isGroupTask: false,
          dataFields: [
            { key: 'flood_events', label: 'Known Flood Events', type: 'number' },
            { key: 'last_flood', label: 'Last Flood Date', type: 'date' },
            { key: 'flood_damage_history', label: 'Flood Damage History', type: 'string' },
            { key: 'high_water_marks', label: 'High Water Marks', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Frequent flooding history' },
            { flag: 'red', condition: 'Recent flood damage' },
          ],
        },
        {
          code: 'flood_insurance',
          title: 'Flood insurance requirements',
          description: 'Insurance requirements and costs',
          isGroupTask: false,
          dataFields: [
            { key: 'insurance_required', label: 'Flood Insurance Required', type: 'boolean' },
            { key: 'annual_premium', label: 'Annual Premium Estimate', type: 'number', unit: 'dollars' },
            { key: 'coverage_available', label: 'Coverage Available', type: 'boolean' },
          ],
          suggestedFlags: [
            { flag: 'red', condition: 'High flood insurance costs' },
            { flag: 'yellow', condition: 'Flood insurance required' },
          ],
        },
        {
          code: 'drainage_assessment',
          title: 'Site drainage assessment',
          description: 'How water flows on/off property',
          isGroupTask: false,
          dataFields: [
            { key: 'drainage_pattern', label: 'Drainage Pattern', type: 'string' },
            { key: 'drainage_issues', label: 'Drainage Issues', type: 'array' },
            { key: 'improvements_needed', label: 'Improvements Needed', type: 'string' },
          ],
        },
      ],
    },
    {
      code: 'fire_risk',
      name: 'Fire Risk',
      items: [
        {
          code: 'wildfire_risk',
          title: 'Wildfire risk assessment',
          description: 'Property wildfire risk level',
          isGroupTask: false,
          dataFields: [
            { key: 'risk_level', label: 'Risk Level', type: 'select', options: [
              { value: 'low', label: 'Low' },
              { value: 'moderate', label: 'Moderate' },
              { value: 'high', label: 'High' },
              { value: 'very_high', label: 'Very High' },
              { value: 'extreme', label: 'Extreme' },
            ]},
            { key: 'wui_designation', label: 'Wildland-Urban Interface', type: 'boolean' },
            { key: 'fire_history', label: 'Fire History', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Extreme wildfire risk' },
            { flag: 'red', condition: 'High wildfire risk' },
            { flag: 'yellow', condition: 'Moderate wildfire risk' },
          ],
        },
        {
          code: 'fire_mitigation',
          title: 'Fire mitigation needs',
          description: 'Defensible space and fire prep',
          isGroupTask: false,
          dataFields: [
            { key: 'defensible_space', label: 'Defensible Space Exists', type: 'boolean' },
            { key: 'mitigation_needed', label: 'Mitigation Needed', type: 'array' },
            { key: 'mitigation_cost', label: 'Est. Mitigation Cost', type: 'number', unit: 'dollars' },
          ],
        },
        {
          code: 'fire_insurance',
          title: 'Fire insurance availability',
          description: 'Homeowners/fire insurance status',
          isGroupTask: false,
          dataFields: [
            { key: 'standard_insurance', label: 'Standard Insurance Available', type: 'boolean' },
            { key: 'fair_plan_needed', label: 'FAIR Plan Needed', type: 'boolean' },
            { key: 'premium_estimate', label: 'Premium Estimate', type: 'number', unit: 'dollars' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Cannot obtain fire insurance' },
            { flag: 'red', condition: 'Only FAIR Plan available' },
          ],
        },
        {
          code: 'fire_department',
          title: 'Fire department coverage',
          description: 'Fire protection services',
          isGroupTask: false,
          dataFields: [
            { key: 'department_name', label: 'Fire Department', type: 'string' },
            { key: 'distance', label: 'Distance', type: 'number', unit: 'miles' },
            { key: 'response_time', label: 'Est. Response Time', type: 'string' },
            { key: 'iso_rating', label: 'ISO Rating', type: 'number' },
            { key: 'volunteer_or_paid', label: 'Volunteer or Paid', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'yellow', condition: 'Poor ISO rating or long response' },
          ],
        },
      ],
    },
    {
      code: 'weather_climate',
      name: 'Weather & Climate',
      items: [
        {
          code: 'climate_zone',
          title: 'Climate zone assessment',
          description: 'Local climate characteristics',
          isGroupTask: false,
          dataFields: [
            { key: 'usda_zone', label: 'USDA Hardiness Zone', type: 'string' },
            { key: 'avg_rainfall', label: 'Avg Annual Rainfall', type: 'number', unit: 'inches' },
            { key: 'growing_season', label: 'Growing Season Days', type: 'number', unit: 'days' },
            { key: 'first_frost', label: 'Avg First Frost', type: 'string' },
            { key: 'last_frost', label: 'Avg Last Frost', type: 'string' },
          ],
        },
        {
          code: 'severe_weather',
          title: 'Severe weather risk',
          description: 'Tornado, hurricane, severe storm risk',
          isGroupTask: false,
          dataFields: [
            { key: 'tornado_risk', label: 'Tornado Risk', type: 'select', options: [
              { value: 'low', label: 'Low' },
              { value: 'moderate', label: 'Moderate' },
              { value: 'high', label: 'High' },
            ]},
            { key: 'hurricane_risk', label: 'Hurricane Risk', type: 'select', options: [
              { value: 'none', label: 'None' },
              { value: 'low', label: 'Low' },
              { value: 'moderate', label: 'Moderate' },
              { value: 'high', label: 'High' },
            ]},
            { key: 'severe_storm_frequency', label: 'Severe Storm Frequency', type: 'string' },
            { key: 'hail_risk', label: 'Hail Risk', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'red', condition: 'High severe weather risk' },
            { flag: 'yellow', condition: 'Moderate severe weather exposure' },
          ],
        },
        {
          code: 'drought_risk',
          title: 'Drought risk',
          description: 'Historical drought patterns',
          isGroupTask: false,
          dataFields: [
            { key: 'drought_frequency', label: 'Drought Frequency', type: 'string' },
            { key: 'current_conditions', label: 'Current Conditions', type: 'select', options: [
              { value: 'normal', label: 'Normal' },
              { value: 'abnormally_dry', label: 'Abnormally Dry' },
              { value: 'moderate_drought', label: 'Moderate Drought' },
              { value: 'severe_drought', label: 'Severe Drought' },
              { value: 'extreme_drought', label: 'Extreme Drought' },
            ]},
            { key: 'impact_on_plans', label: 'Impact on Plans', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'red', condition: 'Severe ongoing drought' },
            { flag: 'yellow', condition: 'Drought-prone area' },
          ],
        },
        {
          code: 'winter_weather',
          title: 'Winter weather considerations',
          description: 'Snow, ice, cold extremes',
          isGroupTask: false,
          dataFields: [
            { key: 'avg_snowfall', label: 'Avg Annual Snowfall', type: 'number', unit: 'inches' },
            { key: 'extreme_cold', label: 'Extreme Cold Events', type: 'string' },
            { key: 'ice_storms', label: 'Ice Storm Risk', type: 'string' },
            { key: 'winter_access_issues', label: 'Winter Access Issues', type: 'boolean' },
          ],
          suggestedFlags: [
            { flag: 'yellow', condition: 'Significant winter weather challenges' },
          ],
        },
      ],
    },
  ],
};

